import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { X, Camera, Loader2 } from 'lucide-react';

export default function BarcodeScanner({ onScanSuccess, onClose }) {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    let html5QrCode;

    const startScanner = async () => {
      try {
        html5QrCode = new Html5Qrcode('barcode-reader');
        
        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          async (decodedText) => {
            setLoading(true);
            await html5QrCode.stop();
            
            try {
              const response = await fetch(`https://world.openfoodfacts.org/api/v0/product/${decodedText}.json`);
              const data = await response.json();
              
              if (data.status === 1 && data.product) {
                const product = data.product;
                const productInfo = {
                  name: product.product_name || product.generic_name || '',
                  brand: product.brands || '',
                  size: product.quantity || '',
                  barcode: decodedText,
                };
                
                toast.success('Product found!');
                onScanSuccess(productInfo);
              } else {
                toast.warning('Product not in database. Fill manually.');
                onScanSuccess({ barcode: decodedText, name: '', brand: '', size: '' });
              }
              onClose();
            } catch (error) {
              toast.error('Failed to fetch product details.');
              onScanSuccess({ barcode: decodedText, name: '', brand: '', size: '' });
              onClose();
            } finally {
              setLoading(false);
            }
          },
          (error) => {
            // Ignore scan errors
          }
        );
        setScanning(true);
      } catch (err) {
        console.error('Camera error:', err);
        toast.error('Camera access denied or not available');
        setTimeout(onClose, 2000);
      }
    };

    startScanner();

    return () => {
      if (html5QrCode && scanning) {
        html5QrCode.stop().catch(console.error);
      }
    };
    // `scanning` is only used to guard cleanup; we don't want to re-start scanner when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScanSuccess, onClose]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl max-w-lg w-full overflow-hidden">
        <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <h3 className="font-heading font-bold">Scan Barcode</h3>
          </div>
          <button onClick={onClose} className="hover:bg-primary-foreground/10 rounded-full p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <p className="text-foreground font-medium">Looking up product...</p>
            </div>
          ) : (
            <>
              <div id="barcode-reader" className="rounded-lg overflow-hidden min-h-[300px]"></div>
              <p className="text-sm text-muted-foreground text-center mt-4">
                Position the barcode within the frame
              </p>
              <Button onClick={onClose} variant="outline" className="w-full mt-4 rounded-full">
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
